/**
 * Hook-local callbacks for usePendingChannelDrops, extracted so the hook file
 * stays under the 200-line FE limit. Each builder returns a memoised callback
 * that wires the React state setters to the pure helper modules.
 */
import { useCallback } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  applyAiRefAdd,
  applyChannelAdd,
  applyDeleteRequest,
  keyOf,
  mergeSnapshot,
} from './pendingDropHelpers'
import { planDelete, planInsert, planReorder } from './pendingPlanHelpers'
import type {
  CurrentSlotsByChannel,
  PendingDrop,
  PendingState,
} from './usePendingChannelDrops'
import type { AddToProductImage, AddToProductTarget } from '../types'

function nextTempKey(target: AddToProductTarget): string {
  const r = Math.random().toString(36).slice(2, 8)
  return target.type === 'channel'
    ? `tmp:c|${target.channelId}|${target.slot}|${r}`
    : `tmp:a|${target.slot}|${r}`
}

export function useAddCallback(s: PendingState, qc: QueryClient, productId: string) {
  return useCallback(
    (
      image: AddToProductImage,
      target: AddToProductTarget,
      currentSlotsByChannel?: CurrentSlotsByChannel,
    ) => {
      if (currentSlotsByChannel) {
        s.setSnapshot((prev) => mergeSnapshot(prev, currentSlotsByChannel))
      }
      if (target.type === 'ai_reference') {
        applyAiRefAdd(s.setPending, image, target, nextTempKey(target))
        return
      }
      const tempKey = nextTempKey(target)
      const accepted = applyChannelAdd(
        s.setPending, s.setShifts, image, target, tempKey, currentSlotsByChannel,
      )
      if (accepted) {
        planInsert(
          { setPlans: s.setPlans, qc, productId },
          target.channelId, target.slot, image, tempKey,
        )
      }
    },
    [s, qc, productId],
  )
}

export function useReorderSlotsCallback(s: PendingState, qc: QueryClient, productId: string) {
  return useCallback(
    (channelId: string, fromSlot: number, toSlot: number) => {
      planReorder({ setPlans: s.setPlans, qc, productId }, channelId, fromSlot, toSlot)
    },
    [s.setPlans, qc, productId],
  )
}

export function useRemoveCallback(s: PendingState) {
  return useCallback(
    (target: AddToProductTarget) => {
      const k = keyOf(target)
      const tempKey = s.pending.get(k)?.tempKey
      s.setPending((prev) => removeKey(prev, k))
      if (target.type === 'channel' && tempKey) {
        s.setPlans((prev) => dropNewFromPlan(prev, target.channelId, tempKey))
      }
    },
    [s],
  )
}

function removeKey(
  m: Map<string, PendingDrop>,
  k: string,
): Map<string, PendingDrop> {
  const next = new Map(m)
  next.delete(k)
  return next
}

function dropNewFromPlan(
  prev: PendingState['plans'],
  channelId: string,
  tempKey: string,
): PendingState['plans'] {
  const plan = prev.get(channelId)
  if (!plan) return prev
  const filtered = plan.filter(
    (it) => it.kind !== 'new' || it.tempKey !== tempKey,
  )
  if (filtered.length === plan.length) return prev
  const next = new Map(prev)
  next.set(channelId, filtered)
  return next
}

export function useRequestDeleteCallback(
  s: PendingState, qc: QueryClient, productId: string,
) {
  return useCallback(
    (target: AddToProductTarget) => {
      applyDeleteRequest(
        s.setPending, s.setShifts, s.setDeletes, s.pending, s.snapshot, target,
      )
      if (target.type === 'channel') {
        planDelete({ setPlans: s.setPlans, qc, productId }, target.channelId, target.slot)
      }
    },
    [s, qc, productId],
  )
}
